import React from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {FiCalendar,FiMapPin,FiClock,FiDollarSign,FiExternalLink,FiMail}=FiIcons;

function Shows() {
  const pastShows = [
    {date: '2023-12-31', venue: 'New Year\'s Eve Spectacular', city: 'Edmonton,AB', note: 'The best New Year\'s party we didn\'t remember!'},
    {date: '2023-11-18', venue: 'The Basement', city: 'Saskatoon,SK', note: 'Literally played in a basement. It was underground in every sense!'},
    {date: '2023-10-14', venue: 'Union Hall', city: 'Edmonton,AB', note: 'Where we united the crowd with questionable dance moves.'},
    {date: '2023-09-23', venue: 'Festival of Rock', city: 'Calgary,AB', note: 'We rocked so hard the cows started headbanging three provinces away.'},
    {date: '2023-08-15', venue: 'Summer Sounds Festival', city: 'Winnipeg,MB', note: 'Summer, sounds, and us. What more could you want?'},
    {date: '2023-07-22', venue: 'The Rock Cellar', city: 'Vancouver,BC', note: 'We were literally in a cellar. It was rock bottom in the best way!'},
    {date: '2023-06-10', venue: 'Midnight Special', city: 'Toronto,ON', note: 'Started at midnight, ended when the sun came up. Time flies when you\'re rocking!'},
    {date: '2023-05-05', venue: 'Cinco de Mayo Fiesta', city: 'Montreal,QC', note: 'We don\'t speak Spanish, but music is universal, right?'},
    {date: '2023-04-01', venue: 'April Fools Bash', city: 'Halifax,NS', note: 'Everyone thought we were kidding when we said we\'d rock their faces off. We weren\'t.'},
    {date: '2023-03-17', venue: 'St. Patrick\'s Day Rager', city: 'Ottawa,ON', note: 'We\'re not Irish, but we drank green beer and played green songs. Close enough!'}
  ];

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {weekday: 'short', month: 'short', day: 'numeric'});
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1 
            className="text-5xl md:text-7xl font-bold bebas-neune gradient-text mb-6"
            initial={{opacity: 0, y: 50}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.8}}
          >
            LIVE SHOWS
          </motion.h1>
          <motion.p 
            className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto mb-8"
            initial={{opacity: 0, y: 30}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.8, delay: 0.2}}
          >
            Where the magic happens and the neighbors complain. Experience Big City Supreme in their natural habitat: loud and live.
          </motion.p>
          <motion.div 
            className="bg-red-900/20 border border-red-500/30 rounded-lg p-6 max-w-2xl mx-auto"
            initial={{opacity: 0, y: 30}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.8, delay: 0.4}}
          >
            <p className="text-red-300 font-semibold mb-2">🎸 Live Show Warning 🥁</p>
            <p className="text-gray-300 text-sm">
              Side effects may include: Uncontrollable air guitar, spontaneous headbanging, temporary hearing enhancement, and an inexplicable urge to start your own band.
            </p>
          </motion.div>
        </div>
      </section>

      {/* No Upcoming Shows */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center"
            initial={{opacity: 0, y: 30}}
            whileInView={{opacity: 1, y: 0}}
            transition={{duration: 0.8}}
            viewport={{once: true}}
          >
            <h2 className="text-4xl md:text-5xl font-bold bebas-neune gradient-text mb-8">
              Tour Status: Currently Undefined
            </h2>
            <div className="bg-black/50 rounded-lg p-8 border border-yellow-500/20 max-w-3xl mx-auto">
              <div className="text-6xl mb-6">🤔</div>
              <h3 className="text-2xl font-bold text-white mb-4">
                Are we touring? Maybe. Are we not touring? Also maybe.
              </h3>
              <p className="text-gray-300 text-lg mb-6">
                We haven't decided yet. The band is currently in a state of quantum uncertainty - 
                simultaneously touring and not touring until someone observes us buying tour bus tickets.
              </p>
              <p className="text-yellow-400 font-semibold mb-8">
                Check back soon. Or don't. We'll probably surprise you either way.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a 
                  href="mailto:bigcitysupreme@gmail.com" 
                  className="flex items-center justify-center space-x-2 bg-red-600 hover:bg-red-700 px-6 py-3 rounded-full font-semibold transition-colors"
                >
                  <SafeIcon icon={FiMail} className="h-5 w-5" />
                  <span>Beg Us to Visit Your City</span>
                </a>
                <button className="flex items-center justify-center space-x-2 border border-yellow-500 hover:bg-yellow-500 hover:text-black px-6 py-3 rounded-full font-semibold transition-colors">
                  <span>Check Social Media for Updates</span>
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Live Experience */}
      <section className="py-20 bg-gradient-to-r from-red-900/20 to-yellow-900/20 noise-bg">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h2 
            className="text-4xl md:text-5xl font-bold bebas-neune text-white mb-8"
            initial={{opacity: 0, y: 30}}
            whileInView={{opacity: 1, y: 0}}
            transition={{duration: 0.8}}
            viewport={{once: true}}
          >
            The Supreme Live Experience
          </motion.h2>
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {[
              {title: 'Matching Outfits', description: '73% of our stage presence comes from coordinated fashion choices. The other 27% is pure talent.', icon: '🎭'},
              {title: 'Electrifying Energy', description: 'We channel raw energy into the crowd like human lightning rods, but with better hair.', icon: '⚡'},
              {title: 'Unforgettable Nights', description: 'Our shows are so memorable, you\'ll still be humming our songs in the shower weeks later.', icon: '🌟'}
            ].map((feature, index) => (
              <motion.div 
                key={index}
                className="bg-black/50 rounded-lg p-6"
                initial={{opacity: 0, y: 30}}
                whileInView={{opacity: 1, y: 0}}
                transition={{duration: 0.6, delay: index * 0.1}}
                viewport={{once: true}}
              >
                <div className="text-4xl mb-4">{feature.icon}</div>
                <h3 className="text-xl font-bold text-white mb-3">{feature.title}</h3>
                <p className="text-gray-300">{feature.description}</p>
              </motion.div>
            ))}
          </div>
          <motion.p 
            className="text-xl text-gray-200 mb-8"
            initial={{opacity: 0, y: 30}}
            whileInView={{opacity: 1, y: 0}}
            transition={{duration: 0.8, delay: 0.4}}
            viewport={{once: true}}
          >
            Don't just take our word for it - come see why Edmonton's finest export (besides oil and hockey players) is creating buzz across Canada.
          </motion.p>
        </div>
      </section>

      {/* Past Shows */}
      <section className="py-20 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2 
            className="text-4xl md:text-5xl font-bold bebas-neune gradient-text text-center mb-12"
            initial={{opacity: 0, y: 30}}
            whileInView={{opacity: 1, y: 0}}
            transition={{duration: 0.8}}
            viewport={{once: true}}
          >
            Recent Conquests
          </motion.h2>
          <div className="grid md:grid-cols-2 gap-4">
            {pastShows.map((show, index) => (
              <motion.div 
                key={index}
                className="bg-gray-900 rounded-lg p-4 border border-gray-700 hover:border-gray-600 transition-colors"
                initial={{opacity: 0, y: 20}}
                whileInView={{opacity: 1, y: 0}}
                transition={{duration: 0.4, delay: index * 0.1}}
                viewport={{once: true}}
              >
                <div className="flex flex-col">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h3 className="font-semibold text-white">{show.venue}</h3>
                      <p className="text-gray-400 text-sm">{show.city}</p>
                    </div>
                    <div className="text-red-400 text-sm font-medium">
                      {formatDate(show.date)}
                    </div>
                  </div>
                  <p className="text-yellow-400 text-xs italic mt-2">
                    "{show.note}"
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Booking */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h2 
            className="text-4xl md:text-5xl font-bold bebas-neune gradient-text mb-8"
            initial={{opacity: 0, y: 30}}
            whileInView={{opacity: 1, y: 0}}
            transition={{duration: 0.8}}
            viewport={{once: true}}
          >
            Book Big City Supreme
          </motion.h2>
          <motion.p 
            className="text-xl text-gray-300 mb-8"
            initial={{opacity: 0, y: 30}}
            whileInView={{opacity: 1, y: 0}}
            transition={{duration: 0.8, delay: 0.2}}
            viewport={{once: true}}
          >
            Want to bring the Supreme experience to your venue? We travel with instruments, matching outfits, and enough attitude to fill any stage.
          </motion.p>
          <motion.div 
            initial={{opacity: 0, y: 30}}
            whileInView={{opacity: 1, y: 0}}
            transition={{duration: 0.8, delay: 0.4}}
            viewport={{once: true}}
          >
            <a 
              href="mailto:bigcitysupreme@gmail.com" 
              className="inline-flex items-center space-x-2 bg-red-600 hover:bg-red-700 px-8 py-4 rounded-full font-bold text-lg transition-colors"
            >
              <SafeIcon icon={FiMail} className="h-6 w-6" />
              <span>Contact for Booking</span>
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default Shows;